// src/pages/NetworkAdminDashboard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import socketService from '../services/socket.service';
import { Role } from '../interfaces/Role';
import { ActivityUpdate, formatTimestamp, getTimeSince, getActivityIcon } from '../utils/activityUtils';
import '../styles/dashboard-animations.css';
import '../styles/modal-fixes.css';
import '../styles/responsive-tables.css';
import UserService from '../services/user.service';
import RoleService from '../services/role.service';
import AuthService from '../services/auth.service';
import CategoryService from '../services/category.service';
import { User } from '../interfaces/User';
import { Category } from '../interfaces/Category';
import AdminHeader from '../components/AdminHeader';
import Footer from '../components/Footer';
import AdminCategoryAssignmentsTable from '../components/AdminCategoryAssignmentsTable';
import CategoryAssignment from '../components/CategoryAssignment';

interface SystemStats {
  networkStatus: string;
  activeSessions: number;
  uptime: string;
}

const NetworkAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentUserRole, setCurrentUserRole] = useState<number>(3); // Default to system_incharge (3)
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [systemStats, setSystemStats] = useState<SystemStats>({
    networkStatus: 'Connecting...',
    activeSessions: 0,
    uptime: '0 days, 0 hours'
  });  
  const [activities, setActivities] = useState<ActivityUpdate[]>([]);  
  const [connected, setConnected] = useState<boolean>(false);
    // Handle logout
  const handleLogout = () => {
    AuthService.logout();
    // Force a full page refresh to ensure clean state
    window.location.href = '/login';
  };
    // Role management state
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<number>(0); // 0 means all roles
  const [pendingRoleUpdates, setPendingRoleUpdates] = useState<Record<string | number, any>>({});  
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' as 'success' | 'danger' | 'warning'
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usersPerPage] = useState<number>(10);
  const [paginatedUsers, setPaginatedUsers] = useState<User[]>([]);    // Category management state
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);
  const [categoryForm, setCategoryForm] = useState<{ title: string; editId?: number }>({ title: '' });
  const [categoryNotification, setCategoryNotification] = useState({
    show: false,
    message: '',
    type: 'success' as 'success' | 'danger' | 'warning'
  });
  
  // Category pagination state
  const [categoryCurrentPage, setCategoryCurrentPage] = useState<number>(1);
  const [categoriesPerPage] = useState<number>(5);
  const [paginatedCategories, setPaginatedCategories] = useState<Category[]>([]);
    // Category assignment state
  const [showAssignmentModal, setShowAssignmentModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  
  // References for animation tracking
  const prevActivityCountRef = useRef<number>(0);
  const prevNetworkStatusRef = useRef<string>('');
  const prevActiveSessionsRef = useRef<number>(0);
  // Fetch users with search functionality
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserService.getUsers(searchTerm, 1, -1);      
      if (response && response.users) {
        setUsers(response.users);
        // Apply role filter if active
        if (roleFilter > 0) {
          setFilteredUsers(response.users.filter((user: User) => user.role_id === roleFilter));
        } else {
          setFilteredUsers(response.users);
        }
        
        if (response.users.length === 0 && searchTerm) {
          showNotification('No users found matching your search criteria', 'warning');
        }
      } else {
        setUsers([]);
        setFilteredUsers([]);
        showNotification('Unable to retrieve user data', 'danger');
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setUsers([]);
      showNotification(`Error fetching users: ${err?.message || 'Unknown error'}`, 'danger');
    } finally {
      setLoading(false);
    }
  };
  // Fetch all available roles
  const fetchRoles = async () => {
    try {      const rolesData = await RoleService.getAllRoles();
      if (rolesData && Array.isArray(rolesData)) {
        setRoles(rolesData);
      } else {
        showNotification('Failed to fetch role data in expected format', 'danger');
      }
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      showNotification(`Failed to fetch roles: ${err?.message || 'Unknown error'}`, 'danger');
    }
  };
  
  // Handle search input with debounce
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };  
    // Handle role update
  const handleRoleUpdate = async (userId: number, roleId: number) => {
    
    // Make sure roles are loaded
    if (!roles || roles.length === 0) {
      try {
        await fetchRoles();
        showNotification('Loaded available roles', 'success');
      } catch (error) {
        showNotification('Failed to load roles. Please try again.', 'danger');
        return;
      }
    }
    
    // Check if user is trying to modify their own role
    if (pendingRoleUpdates.currentUserId && userId === pendingRoleUpdates.currentUserId) {
      showNotification('You cannot change your own role', 'danger');
      return;
    }    // Check if the current user has permission to assign this role
    // System Incharge (3) can assign any role
    // Super Admin (5) cannot assign roles
    // Admin (2) and other roles cannot assign roles
    if (currentUserRole === 3) {
      // System Incharge can assign any role
    } else if (currentUserRole === 5) {
      // Super Admin cannot assign roles
      showNotification('Super Admins do not have permission to assign roles', 'danger');
      return;
    } else {
      // Other roles (admin, developer, user) cannot assign roles
      showNotification('You do not have permission to assign roles', 'danger');
      return;
    }

    // Find user to update
    const userToUpdate = users.find((u: User) => u.user_id === userId);
    
    // Safety check in case user not found
    if (!userToUpdate) {
      showNotification('User not found', 'danger');
      return;
    }    // Check if trying to modify a higher-level role
    // System Incharge (3) can modify anyone
    // Super Admin (5) cannot modify user roles
    // Admin (2) and other roles cannot modify user roles
    if (currentUserRole === 3) {
      // System Incharge can modify anyone
    } else if (currentUserRole === 5) {
      // Super Admin cannot modify user roles
      showNotification('Super Admins do not have permission to modify user roles', 'danger');
      return;
    } else {
      // Admin, developer, and user roles cannot modify user roles
      showNotification('You do not have permission to modify user roles', 'danger');
      return;
    }

    // Check if trying to assign the same role
    if (userToUpdate.role_id === roleId) {
      showNotification('User already has this role', 'warning');
      return;
    }

    // Mark user as being updated
    setPendingRoleUpdates(prev => ({ ...prev, [userId]: true }));
    
    try {
      // Call the API to update user role
      const updatedUser = await UserService.updateUserRole(userId, roleId);
      
      // Make a copy of current users to update
      const updatedUsers = [...users];
      
      // Find user in the array and update role
      const userIndex = updatedUsers.findIndex((u: User) => u.user_id === userId);
      if (userIndex !== -1) {
        updatedUsers[userIndex] = { ...updatedUsers[userIndex], role_id: updatedUser.role_id };
      }
        
      // Update users state
      setUsers(updatedUsers);
      
      // Update filtered users based on current filter
      if (roleFilter === 0) {
        // No filter, show all users
        setFilteredUsers(updatedUsers);
      } else {
        // Only show users matching the selected role
        setFilteredUsers(updatedUsers.filter((u: User) => u.role_id === roleFilter));
      }
      
      // Create activity log
      const newRoleName = getRoleName(roleId);
      const activityUpdate: ActivityUpdate = {
        type: 'roleChange',
        message: `${userToUpdate.first_name} ${userToUpdate.last_name}'s role updated to ${newRoleName}`,
        timestamp: new Date()
      };
      
      // Add to activity feed
      setActivities(prev => [activityUpdate, ...prev].slice(0, 10));
      
      // Show success message
      showNotification(`Role successfully updated to ${newRoleName}`, 'success');    
    } catch (err: any) {
      console.error('Role update error:', err);
      let errorMessage = 'Failed to update role';
      
      if (err?.message) {
        errorMessage += `: ${err.message}`;
      } else if (err?.response?.data?.message) {
        errorMessage += `: ${err.response.data.message}`;
      }
      
      // Add error to activity feed for visibility
      const errorActivity: ActivityUpdate = {
        type: 'system',
        message: `Error updating ${userToUpdate.first_name}'s role: ${errorMessage}`,
        timestamp: new Date()
      };
      
      setActivities(prev => [errorActivity, ...prev].slice(0, 10));
      
      // Show notification
      showNotification(errorMessage, 'danger');
    } finally {
      // Always clear the pending status
      setPendingRoleUpdates(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Show notification helper
  const showNotification = (message: string, type: 'success' | 'danger' | 'warning') => {
    setNotification({
      show: true,
      message,
      type
    });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setNotification(prev => ({
        ...prev,
        show: false
      }));
    }, 3000);
  };

  // Get role name from role_id
  const getRoleName = (roleId: number) => {
    const role = roles.find(r => r.role_id === roleId);
    if (role) {
      return role.title;
    }    // Fallback role names
    const fallbackRoles: Record<number, string> = {
      1: 'user',
      2: 'admin',
      3: 'system_incharge',
      4: 'developer',
      5: 'super_admin'
    };
    
    return fallbackRoles[roleId] || 'Unknown';
  };  
  
  // Effect for searching when searchTerm changes
  useEffect(() => {
    // Create a debounce for search
    const debounceTimeout = setTimeout(() => {
      if (searchTerm !== '') {
        fetchUsers();
      }
    }, 500);
    
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm]);
    // Effect to update filteredUsers when users or roleFilter change
  useEffect(() => {
    if (roleFilter === 0) {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(users.filter((user: User) => user.role_id === roleFilter));
    }
  }, [users, roleFilter]);

  // Effect to update pagination when filteredUsers change
  useEffect(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    setPaginatedUsers(filteredUsers.slice(startIndex, endIndex));
  }, [filteredUsers, currentPage, usersPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle role change in select dropdown
  const handleRoleChange = (userId: number, newRoleId: number) => {
    setPendingRoleUpdates(prev => ({
      ...prev,
      [userId]: newRoleId
    }));
  };
  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter]);

  // Effect to update category pagination when categories change
  useEffect(() => {
    const startIndex = (categoryCurrentPage - 1) * categoriesPerPage;
    const endIndex = startIndex + categoriesPerPage;
    setPaginatedCategories(categories.slice(startIndex, endIndex));
  }, [categories, categoryCurrentPage, categoriesPerPage]);

  // Calculate total category pages
  const totalCategoryPages = Math.ceil(categories.length / categoriesPerPage);

  // Category pagination handlers
  const handleCategoryPageChange = (page: number) => {
    setCategoryCurrentPage(page);
  };

  const handleCategoryPreviousPage = () => {
    if (categoryCurrentPage > 1) {
      setCategoryCurrentPage(categoryCurrentPage - 1);
    }
  };

  const handleCategoryNextPage = () => {
    if (categoryCurrentPage < totalCategoryPages) {
      setCategoryCurrentPage(categoryCurrentPage + 1);
    }
  };
  // Category management functions
  const fetchCategories = async () => {
    try {
      setCategoryLoading(true);
      setCategoryCurrentPage(1); // Reset to first page when fetching categories
      const response = await CategoryService.getAllCategories();
      if (response && (response.categories || response.data)) {
        setCategories(response.categories || response.data || []);
      } else {
        setCategories([]);
        showCategoryNotification('Unable to retrieve category data', 'warning');
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setCategories([]);
      showCategoryNotification(`Error: ${err?.message || 'Failed to fetch categories'}`, 'danger');
    } finally {
      setCategoryLoading(false);
    }
  };
  
  const handleCategoryFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!categoryForm.title.trim()) {
      showCategoryNotification('Category title cannot be empty', 'warning');
      return;
    }
    
    setCategoryLoading(true);
    
    try {
      if (categoryForm.editId) {
        // Update existing category
        await CategoryService.updateCategory(categoryForm.editId, categoryForm.title);
        showCategoryNotification('Category updated successfully', 'success');      } else {
        // Create new category with default faculty_id = 1
        await CategoryService.createCategory(categoryForm.title, 1);
        showCategoryNotification('Category created successfully', 'success');
      }
      
      // Reset form and refresh list
      setCategoryForm({ title: '' });
      fetchCategories();
      
    } catch (err: any) {
      console.error('Error saving category:', err);
      showCategoryNotification(`Error: ${err?.message || 'Failed to save category'}`, 'danger');
    } finally {
      setCategoryLoading(false);
    }
  };
  
  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    
    setCategoryLoading(true);
    
    try {
      await CategoryService.deleteCategory(id);
      showCategoryNotification('Category deleted successfully', 'success');
      fetchCategories();
    } catch (err: any) {
      console.error('Error deleting category:', err);
      showCategoryNotification(`Error: ${err?.message || 'Failed to delete category'}`, 'danger');
    } finally {
      setCategoryLoading(false);
    }
  };
  
  const startEditCategory = (category: Category) => {
    setCategoryForm({
      title: category.title,
      editId: category.id
    });
  };
  
  const cancelEditCategory = () => {
    setCategoryForm({ title: '' });
  };
  
  const showCategoryNotification = (message: string, type: 'success' | 'danger' | 'warning') => {
    setCategoryNotification({
      show: true,
      message,
      type
    });
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setCategoryNotification(prev => ({
        ...prev,
        show: false
      }));
    }, 3000);
  };
  useEffect(() => {// Get user from localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        if (user && user.role_id) {
          setCurrentUserRole(user.role_id);
          
          // Store current user ID for later use
          // This will help prevent users from changing their own roles
          if (user.user_id) {
            setPendingRoleUpdates(prev => ({
              ...prev,
              currentUserId: user.user_id
            }));
          }        } else {
          // Use default role of system_incharge (3) if not found
          setCurrentUserRole(3);
        }
      } else {
        // No user found in localStorage, using default role 3
        setCurrentUserRole(3);
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      // Fallback to default role
    }    // First fetch roles, then users and categories    // This ensures roles are available when rendering user role options
    fetchRoles()
      .then(() => {
        return fetchUsers();
      })
      .then(() => {
        return fetchCategories();
      })
      .catch(error => {
        console.error('Error during initial data loading:', error);
      });

    // Connect to WebSocket
    socketService.connect();
    
    // Subscribe to connection status
    const connectionCheckInterval = setInterval(() => {
      setConnected(socketService.isConnected());
    }, 2000);
      
    // Listen for system stats updates
    const statsUnsubscribe = socketService.addListener<SystemStats>('systemStats', (stats) => {
      // Save previous values for animation triggering
      prevNetworkStatusRef.current = systemStats.networkStatus;
      prevActiveSessionsRef.current = systemStats.activeSessions;
      
      // Update stats
      setSystemStats(stats);
      
      // Apply animations to changed values
      if (prevNetworkStatusRef.current !== stats.networkStatus) {
        const statusBadge = document.querySelector('.status-badge');
        if (statusBadge) {
          statusBadge.classList.add('stats-value-change');
          setTimeout(() => statusBadge.classList.remove('stats-value-change'), 1000);
        }
      }
      
      if (prevActiveSessionsRef.current !== stats.activeSessions) {
        const sessionElement = document.querySelectorAll('.stats-value')[0]; 
        if (sessionElement) {
          sessionElement.classList.add('stats-value-change');
          setTimeout(() => sessionElement.classList.remove('stats-value-change'), 1000);
        }
      }
    });
    
    // Listen for activity updates
    const activityUnsubscribe = socketService.addListener<ActivityUpdate>('activityUpdate', (activity) => {
      prevActivityCountRef.current = activities.length;
      setActivities(prev => [activity, ...prev].slice(0, 10)); // Keep only 10 most recent activities
    });
      // Cleanup on unmount
    return () => {
      statsUnsubscribe();
      activityUnsubscribe();
      clearInterval(connectionCheckInterval);
      socketService.disconnect();
    };
  }, []);    return (
    <div className="d-flex flex-column min-vh-100">
      <AdminHeader user={currentUser || {email: ''}} />
      
      <div className="container-fluid mt-3 flex-grow-1 px-2 px-sm-3 px-lg-4">
        <div className="row">
          <div className="col-12">
            {/* Live Status Header - Responsive */}
            <div className="card mb-3 mb-md-4">
              <div className="card-header bg-primary text-white">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                  <h4 className="m-0 fs-5 fs-md-4">Network Admin Dashboard</h4>
                  <div className="d-flex align-items-center gap-2 gap-sm-3">
                    <span className={`badge ${connected ? 'bg-success' : 'bg-warning'} px-2 py-1 fs-6`} 
                          title={connected ? 'Real-time connection active' : 'Connecting to real-time updates...'}>
                      <i className={`bi ${connected ? 'bi-wifi' : 'bi-wifi-off'} me-1`}></i>
                      <span className="d-none d-sm-inline">{connected ? 'Live Updates Active' : 'Connecting...'}</span>
                      <span className="d-sm-none">{connected ? 'Live' : 'Offline'}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Row */}
        <div className="row g-3 g-lg-4">
          <div className="col-12">
            {/* Role Management Panel - Responsive */}
            <div className="card mb-3 mb-md-4">            
              <div className="card-header bg-primary text-white">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                  <h3 className="m-0 fs-5 fs-md-4">User Role Management</h3>
                  <button 
                    className="btn btn-sm btn-light d-flex align-items-center" 
                    onClick={() => {
                      setSearchTerm('');
                      fetchUsers();
                    }}
                    title="Refresh user list"
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    <span className="d-none d-sm-inline">Refresh</span>
                  </button>
                </div>
              </div>
              <div className="card-body p-2 p-sm-3">
                {notification.show && (
                  <div className={`alert alert-${notification.type} alert-dismissible fade show mb-3`} role="alert">
                    {notification.message}
                    <button type="button" className="btn-close" onClick={() => setNotification({...notification, show: false})}></button>
                  </div>
                )}
                
                {/* Search and Filter Row - Responsive */}
                <div className="row g-2 mb-3">
                  <div className="col-12 col-md-8">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setSearchTerm('')}
                        disabled={!searchTerm}
                      >
                        <i className="bi bi-x-lg"></i>
                        <span className="d-none d-sm-inline ms-1">Clear</span>
                      </button>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">                    <select 
                      className="form-select"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(parseInt(e.target.value))}
                    >
                      <option value="0">All Roles</option>
                      {roles.map(role => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>                {/* User Stats - Responsive */}
                <div className="alert alert-info mb-3 stats-compact">
                  <div className="row text-center">
                    <div className="col-6 col-sm-3">
                      <strong>{users.length}</strong>
                      <div className="small text-muted">Total Users</div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <strong>{filteredUsers.length}</strong>
                      <div className="small text-muted">Filtered</div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <strong>{paginatedUsers.length}</strong>
                      <div className="small text-muted">Showing</div>
                    </div>
                    <div className="col-6 col-sm-3">
                      <strong>{totalPages}</strong>
                      <div className="small text-muted">Pages</div>
                    </div>
                  </div>
                  {filteredUsers.length > usersPerPage && (
                    <div className="text-center mt-2">
                      <small className="text-muted">Page {currentPage} of {totalPages}</small>
                    </div>
                  )}
                </div>

                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2 text-muted">Loading users...</div>
                  </div>
                ) : (
                  <>                    {/* Users Table - Responsive */}
                    <div className="table-responsive table-container-optimized">
                      <table className="table table-striped table-hover table-sm admin-table">
                        <thead className="table-dark">
                          <tr>
                            <th className="d-none d-md-table-cell col-id">ID</th>
                            <th className="col-user">User</th>
                            <th className="d-none d-lg-table-cell col-email">Email</th>
                            <th className="col-role">Role</th>
                            <th className="text-center col-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.length > 0 ? (
                            paginatedUsers.map((user) => (
                              <tr key={user.user_id}>
                                <td className="d-none d-md-table-cell col-id">{user.user_id}</td>
                                <td className="col-user">
                                  <div>
                                    <div className="fw-medium text-truncate-custom">{user.first_name} {user.last_name}</div>
                                    <div className="small text-muted d-lg-none text-truncate-custom">{user.email}</div>
                                  </div>
                                </td>
                                <td className="d-none d-lg-table-cell col-email">
                                  <small className="text-truncate-custom">{user.email}</small>
                                </td>
                                <td className="col-role">
                                  <select
                                    className="form-select form-select-sm"
                                    value={pendingRoleUpdates[user.user_id] || user.role_id}
                                    onChange={(e) => handleRoleChange(user.user_id, parseInt(e.target.value))}
                                    disabled={loading}
                                  >
                                    {roles.map(role => (
                                      <option key={role.role_id} value={role.role_id}>
                                        {role.title}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="text-center col-actions">
                                  <div className="d-flex justify-content-center btn-group-compact">
                                    {pendingRoleUpdates[user.user_id] && 
                                     pendingRoleUpdates[user.user_id] !== user.role_id && (
                                      <>
                                        <button
                                          className="btn btn-success btn-sm"
                                          onClick={() => handleRoleUpdate(user.user_id, pendingRoleUpdates[user.user_id])}
                                          disabled={loading}
                                          title="Save changes"
                                        >
                                          <i className="bi bi-check-lg"></i>
                                        </button>
                                        <button
                                          className="btn btn-secondary btn-sm"
                                          onClick={() => {
                                            const updated = { ...pendingRoleUpdates };
                                            delete updated[user.user_id];
                                            setPendingRoleUpdates(updated);
                                          }}
                                          title="Cancel changes"
                                        >
                                          <i className="bi bi-x-lg"></i>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-3">
                                {searchTerm || roleFilter !== 0 ? (
                                  <>
                                    <div className="text-muted mb-2">No users found matching your criteria.</div>
                                    <button 
                                      className="btn btn-sm btn-outline-secondary" 
                                      onClick={() => {
                                        setSearchTerm('');
                                        setRoleFilter(0);
                                      }}
                                    >
                                      <i className="bi bi-x-circle me-1"></i> Clear Filters
                                    </button>
                                  </>
                                ) : (
                                  <div className="text-muted">No users found.</div>
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* User Pagination Controls - Responsive */}
                    {filteredUsers.length > usersPerPage && (
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                        <div className="text-muted text-center text-sm-start">
                          <small>
                            Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
                          </small>
                        </div>                        <nav aria-label="User pagination">
                          <ul className="pagination pagination-sm pagination-compact mb-0 justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                              >
                                <i className="bi bi-chevron-left"></i>
                                <span className="d-none d-sm-inline ms-1">Previous</span>
                              </button>
                            </li>
                            
                            {/* Page numbers */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                // Show first page, last page, current page, and pages around current
                                return page === 1 || 
                                       page === totalPages || 
                                       Math.abs(page - currentPage) <= 1;
                              })
                              .map((page, index, array) => {
                                // Add ellipsis if there's a gap
                                const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                                
                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsisBefore && (
                                      <li className="page-item disabled d-none d-sm-block">
                                        <span className="page-link">...</span>
                                      </li>
                                    )}
                                    <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                      <button 
                                        className="page-link" 
                                        onClick={() => handlePageChange(page)}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  </React.Fragment>
                                );
                              })
                            }
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                              >
                                <span className="d-none d-sm-inline me-1">Next</span>
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Category Management Panel - Responsive */}
            <div className="card mb-3 mb-md-4">
              <div className="card-header bg-primary text-white">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                  <h3 className="m-0 fs-5 fs-md-4">Category Management</h3>
                  <div className="d-flex flex-wrap gap-2">
                    <button 
                      className="btn btn-sm btn-info d-flex align-items-center" 
                      onClick={() => setShowAssignmentModal(true)}
                      title="Assign categories to admin users"
                    >
                      <i className="bi bi-people me-1"></i>
                      <span className="d-none d-sm-inline">Assign Categories</span>
                      <span className="d-sm-none">Assign</span>
                    </button>
                    <button 
                      className="btn btn-sm btn-light d-flex align-items-center" 
                      onClick={fetchCategories}
                      title="Refresh categories"
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      <span className="d-none d-sm-inline">Refresh</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body p-2 p-sm-3">
                {categoryNotification.show && (
                  <div className={`alert alert-${categoryNotification.type} alert-dismissible fade show mb-3`} role="alert">
                    {categoryNotification.message}
                    <button type="button" className="btn-close" onClick={() => setCategoryNotification({...categoryNotification, show: false})}></button>
                  </div>
                )}
                
                {/* Category Form - Responsive */}
                <form onSubmit={handleCategoryFormSubmit} className="mb-4">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-8">
                      <label htmlFor="categoryTitle" className="form-label small">Category Title</label>
                      <input 
                        type="text"
                        id="categoryTitle"
                        className="form-control"
                        placeholder="Enter category name"
                        value={categoryForm.title}
                        onChange={(e) => setCategoryForm({...categoryForm, title: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="d-flex gap-2 w-100">
                        <button type="submit" className="btn btn-primary flex-fill" disabled={categoryLoading}>
                          <i className="bi bi-plus-circle me-1"></i>
                          {categoryForm.editId ? 'Update' : 'Add'}
                        </button>
                        {categoryForm.editId && (
                          <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={cancelEditCategory}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </form>

                {/* Category Stats */}
                <div className="alert alert-info mb-3">
                  <div className="row text-center">
                    <div className="col-4">
                      <strong>{categories.length}</strong>
                      <div className="small text-muted">Total Categories</div>
                    </div>
                    <div className="col-4">
                      <strong>{paginatedCategories.length}</strong>
                      <div className="small text-muted">Showing</div>
                    </div>
                    <div className="col-4">
                      <strong>{totalCategoryPages}</strong>
                      <div className="small text-muted">Pages</div>
                    </div>
                  </div>
                  {categories.length > categoriesPerPage && (
                    <div className="text-center mt-2">
                      <small className="text-muted">Page {categoryCurrentPage} of {totalCategoryPages}</small>
                    </div>
                  )}
                </div>                {/* Categories Table - Responsive */}
                <div className="table-responsive table-container-optimized">
                  <table className="table table-striped table-hover table-sm category-table">                    <thead className="table-dark">
                      <tr>
                        <th className="d-none d-md-table-cell col-id">ID</th>
                        <th className="col-title">Category Title</th>
                        <th className="text-center col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCategories.length > 0 ? (                        paginatedCategories.map(category => (
                          <tr key={category.id}>
                            <td className="d-none d-md-table-cell col-id">{category.id}</td>
                            <td className="col-title">
                              <div className="fw-medium">{category.title}</div>
                              <div className="small text-muted d-md-none">ID: {category.id}</div>
                            </td>
                            <td className="col-actions">
                              <div className="d-flex justify-content-center gap-1 flex-wrap btn-group-compact">
                                <button 
                                  type="button" 
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => startEditCategory(category)}
                                  title="Edit category"
                                >
                                  <i className="bi bi-pencil"></i>
                                  <span className="d-none d-lg-inline ms-1">Edit</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => {
                                    setSelectedCategory(category);
                                    setShowAssignmentModal(true);
                                  }}
                                  title="Assign to users"
                                >
                                  <i className="bi bi-people"></i>
                                  <span className="d-none d-lg-inline ms-1">Assign</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteCategory(category.id)}
                                  title="Delete category"
                                >
                                  <i className="bi bi-trash"></i>
                                  <span className="d-none d-lg-inline ms-1">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center py-3">
                            <div className="text-muted">
                              {categories.length === 0 ? 'No categories found' : 'No categories on this page'}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Category Pagination Controls - Responsive */}
                {categories.length > categoriesPerPage && (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                    <div className="text-muted text-center text-sm-start">
                      <small>
                        Showing {((categoryCurrentPage - 1) * categoriesPerPage) + 1} to {Math.min(categoryCurrentPage * categoriesPerPage, categories.length)} of {categories.length} categories
                      </small>
                    </div>                    <nav aria-label="Category pagination">
                      <ul className="pagination pagination-sm pagination-compact mb-0 justify-content-center">
                        <li className={`page-item ${categoryCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={handleCategoryPreviousPage}
                            disabled={categoryCurrentPage === 1}
                          >
                            <i className="bi bi-chevron-left"></i>
                            <span className="d-none d-sm-inline ms-1">Previous</span>
                          </button>
                        </li>
                        
                        {/* Page numbers */}
                        {Array.from({ length: totalCategoryPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return page === 1 || 
                                   page === totalCategoryPages || 
                                   Math.abs(page - categoryCurrentPage) <= 1;
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <li className="page-item disabled d-none d-sm-block">
                                    <span className="page-link">...</span>
                                  </li>
                                )}
                                <li className={`page-item ${categoryCurrentPage === page ? 'active' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handleCategoryPageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              </React.Fragment>
                            );
                          })
                        }
                        
                        <li className={`page-item ${categoryCurrentPage === totalCategoryPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={handleCategoryNextPage}
                            disabled={categoryCurrentPage === totalCategoryPages}
                          >
                            <span className="d-none d-sm-inline me-1">Next</span>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>

            {/* Admin-Category Assignments Table - Responsive */}
            <div className="card mb-3 mb-md-4">
              <div className="card-header bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="m-0 fs-5 fs-md-4">Admin-Category Assignments</h3>
                </div>
              </div>
              <div className="card-body p-2 p-sm-3">
                <AdminCategoryAssignmentsTable />
              </div>
            </div>
          </div>
        </div>
        
        {/* Real-time dashboard sections - Responsive */}
        <div className="row g-3 g-lg-4 mt-2">
          <div className="col-12 col-lg-6">
            <div className="card dashboard-card h-100">
              <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 fs-6 fs-md-5">System Statistics</h4>
                  <small className="text-muted">Real-time data</small>
                </div>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                      <span className="fw-medium">Network Status:</span>
                      <span className={`badge status-badge fs-6 ${systemStats.networkStatus === 'Online' ? 'bg-success' : 'bg-danger'}`}>
                        <i className={`bi ${systemStats.networkStatus === 'Online' ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                        {systemStats.networkStatus}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="fw-bold stats-value fs-4 text-primary">{systemStats.activeSessions}</div>
                      <div className="small text-muted">Active Sessions</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="fw-bold stats-value fs-6 text-success">{systemStats.uptime}</div>
                      <div className="small text-muted">Server Uptime</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-12 col-lg-6">
            <div className="card dashboard-card h-100">
              <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 fs-6 fs-md-5">Recent Activities</h4>
                  <small className="text-muted">Live updates</small>
                </div>
              </div>
              <div className="card-body">
                {activities.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-activity display-4 text-muted mb-2"></i>
                    <p className="mb-0">No recent activities</p>
                  </div>
                ) : (
                  <div className="activity-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {activities.map((activity, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-start mb-2 p-2 bg-light rounded">
                        <div className="flex-grow-1">
                          <span className="me-2" aria-hidden="true">{getActivityIcon(activity.type)}</span>
                          <span className="small">{activity.message}</span>
                        </div>
                        <span className="badge bg-primary rounded-pill small" title={formatTimestamp(activity.timestamp)}>
                          {getTimeSince(activity.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Assignment Modal - Responsive */}
      {showAssignmentModal && (
        <div className="modal-wrapper">
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    {selectedCategory ? (
                      <>Assign Category: <strong>{selectedCategory.title}</strong></>
                    ) : (
                      <>Assign Categories to Admin Users</>
                    )}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    aria-label="Close"
                    onClick={() => setShowAssignmentModal(false)}
                    style={{ opacity: 1 }}
                  ></button>
                </div>
                <div className="modal-body p-2 p-sm-3">
                  <CategoryAssignment 
                    currentUserRole={currentUserRole} 
                    currentUserId={currentUser?.user_id || 0}
                    selectedCategory={selectedCategory}
                  />
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAssignmentModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default NetworkAdminDashboard;
